import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TFunction } from 'i18next';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import { CellWithChildren, DateTimeCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { Icon } from '@components-clean/core/icons/icon';
import { convertDateTimeToMoment } from '@utils-clean/date-time-helpers';
import { stringDateWithoutTimeFromUtcNonNullable } from '@utils/convertor';

export const datasetInfoColumns = (t: TFunction): GridColumnProps[] => {
    const { TooltipCell } = createGridCustomCells();
    return [
        {
            field: 'isActive',
            title: t('grid.columns.isActive'),
            locked: true,
            filter: 'boolean',
            cells: {
                data: (props) => {
                    const row = props.dataItem;
                    return (
                        <CellWithChildren {...props}>
                            <FontAwesomeIcon icon={['far', row.isActive ? 'eye' : 'eye-slash']} />
                        </CellWithChildren>
                    );
                },
            },
            minWidth: 20,
        },
        {
            field: 'date',
            title: t('grid.columns.date'),
            locked: true,
            filter: 'date',
            cellName: 'DateCell',
            cells: {
                data: (props) => {
                    const row = props.dataItem;
                    return (
                        <CellWithChildren {...props}>
                            <span
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                <div className="ellipsis-overflow">
                                    {convertDateTimeToMoment(new Date(row.date), 'DD-MMM-yyyy')}
                                </div>
                                {row.isMigrated && (
                                    <Icon
                                        dataTestId="migrated-icon"
                                        title={`Migrated on ${stringDateWithoutTimeFromUtcNonNullable(
                                            row.migratedOn,
                                            'DD-MMM-yyyy',
                                        )}`}
                                        icon={['far', 'file-import']}
                                        tooltipPosition="top"
                                    />
                                )}
                            </span>
                        </CellWithChildren>
                    );
                },
            },
            minWidth: 50,
        },

        {
            title: t('grid.columns.automationStatus'),
            minWidth: 170,
            // Non-existent field, needed to use column visibility
            field: t('transformedAutomationDetails.statuses'),
            children: [
                {
                    title: t('grid.columns.siteStatus'),
                    field: 'transformedAutomationDetails.siteStatus',
                    minWidth: 85,
                },
                {
                    title: t('grid.columns.controlsStatus'),
                    field: 'transformedAutomationDetails.controlsStatus',
                    minWidth: 85,
                },
            ],
        },

        {
            field: 'createdBy',
            title: t('grid.columns.createdBy'),
            minWidth: 100,
        },
        {
            field: 'createdOn',
            title: t('grid.columns.createdOn'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 50,
            cellName: 'DateCell',
        },
        {
            field: 'dataFilePath',
            title: t('grid.columns.dataFilePath'),
            minWidth: 150,
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            field: 'orgImportFileVersion',
            title: t('grid.columns.orgImportFileVersion'),
            minWidth: 40,
        },
        {
            field: 'studyImportFileVersion',
            title: t('grid.columns.studyImportFileVersion'),
            minWidth: 40,
        },
        {
            field: 'orgPluginVersion',
            title: t('grid.columns.orgPluginVersion'),
            minWidth: 40,
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            field: 'studyPluginVersion',
            title: t('grid.columns.studyPluginVersion'),
            minWidth: 40,
            cells: { data: TooltipCell({ isCsv: false }) },
        },
        {
            field: 'opraVersion',
            title: t('grid.columns.opraVersion'),
            minWidth: 325,
            cells: { data: TooltipCell({ isCsv: false }) },
        },
    ];
};
